import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { IsoGrid, IsoIllustration } from "@/components/site/Iso";
import { SiteHeader } from "@/components/site/SiteHeader";
import { CookieConsent } from "@/components/site/CookieConsent";
import { use, useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { dirFor, normalizeLocale } from "@/lib/i18n";
import { ensureDict, translate } from "@/lib/i18n-dict";
import { getAnalyticsId } from "@/lib/analytics.functions";

function localeForPath(pathname: string) {
  return normalizeLocale(pathname.split("/").filter(Boolean)[0]);
}

function NotFoundComponent() {
  const locale = useRouterState({ select: (state) => localeForPath(state.location.pathname) });
  const t = (text: string) => translate(locale, text);
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="relative isolate flex flex-1 items-center justify-center px-4 py-12">
        <IsoGrid intensity="strong" />
        <div className="max-w-lg text-center">
          <IsoIllustration
            name="error404"
            alt={t("Isometric 404 illustration")}
            className="mx-auto w-full max-w-sm"
            parallax={false}
          />
          <h1 className="mt-6 font-display text-3xl font-bold text-foreground md:text-4xl">
            {t("Page not found")}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {t("The page you're looking for doesn't exist or has been moved.")}
          </p>
          <div className="mt-8">
            <Link
              to={"/" as never}
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {t("Go home")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const locale = useRouterState({ select: (state) => localeForPath(state.location.pathname) });
  const t = (text: string) => translate(locale, text);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {t("This page didn't load")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("Something went wrong on our end. You can try refreshing or head back home.")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("Try again")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("Go home")}
          </a>
        </div>
      </div>
    </div>
  );
}

const FONT_CSS: string[] = [
  "https://api.fontshare.com/v2/css?f%5B%5D=eudoxus-sans@400,500,700,800&f%5B%5D=author@400,500,600&display=swap",
  "https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700;800&family=Noto+Sans+Devanagari:wght@400;500;600;700;800&display=swap",
];

// Microsoft Clarity project ID (public — appears in page HTML anyway).
const CLARITY_ID = "x1be7s5qlu";

const CLARITY_SCRIPT = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", ${JSON.stringify(CLARITY_ID)});`;

// Binotel GetCall widget — Ukrainian localization only.
const BINOTEL_WIDGET_HASH = "8wa2wbmukx6vlwfn9o6f";

const injectBinotel = () => {
  const w = window as unknown as { __binotelGetcallLoaded?: boolean };
  if (w.__binotelGetcallLoaded) return;
  w.__binotelGetcallLoaded = true;
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.async = true;
  s.src = `https://widgets.binotel.com/getcall/widgets/${BINOTEL_WIDGET_HASH}.js`;
  document.body.appendChild(s);
};

// Runs inline at the end of <body> on every SSR page; loads the widget
// only when the page is Ukrainian.
const BINOTEL_INLINE = `(function(){try{if(document.documentElement.lang==='uk'&&!window.__binotelGetcallLoaded){window.__binotelGetcallLoaded=true;var s=document.createElement('script');s.type='text/javascript';s.async=true;s.src=${JSON.stringify(`https://widgets.binotel.com/getcall/widgets/${BINOTEL_WIDGET_HASH}.js`)};document.body.appendChild(s);}}catch(e){}})();`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  staticData: { sitemap: false },
  // Runs on every navigation so the target locale's dictionary is ready
  // before any page renders (the loader below is cached and never reruns).
  beforeLoad: async ({ location }) => {
    await ensureDict(localeForPath(location.pathname));
    return { dictReady: true as const };
  },
  loader: async ({ location }) => {
    await ensureDict(localeForPath(location.pathname));
    return { gaId: await getAnalyticsId().catch(() => null) };
  },
  staleTime: Infinity,
  shouldReload: false,
  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "factory.online" },
      {
        name: "description",
        content:
          "Cloud-based, plug-and-play system for sales, production and inventory in real time — built for manufacturers.",
      },
      { name: "author", content: "factory.online" },
      { property: "og:site_name", content: "factory.online" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        children:
          "try{if(localStorage.getItem('fo-theme')==='light'){document.documentElement.classList.remove('dark')}}catch(e){}",
      },
      {
        // Load web font stylesheets without blocking first paint (fonts use display=swap).
        children: `(function(){${JSON.stringify(FONT_CSS)}.forEach(function(h){var l=document.createElement('link');l.rel='stylesheet';l.href=h;document.head.appendChild(l);});})();`,
      },
      ...(CLARITY_ID ? [{ children: CLARITY_SCRIPT }] : []),
      ...(loaderData?.gaId
        ? [
            { src: `https://www.googletagmanager.com/gtag/js?id=${loaderData.gaId}`, async: true },
            {
              children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config',${JSON.stringify(loaderData.gaId)});`,
            },
          ]
        : []),
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://api.fontshare.com" },
      { rel: "preconnect", href: "https://cdn.fontshare.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      ...FONT_CSS.map((href) => ({ rel: "preload", as: "style", href })),
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const locale = useRouterState({ select: (state) => localeForPath(state.location.pathname) });
  return (
    <html lang={locale} dir={dirFor(locale)} className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
        <script dangerouslySetInnerHTML={{ __html: BINOTEL_INLINE }} />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const locale = useRouterState({ select: (state) => localeForPath(state.location.pathname) });
  // During hydration the active locale's dictionary may still be downloading.
  use(ensureDict(locale) as Promise<void>);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { gaId } = Route.useLoaderData();
  const firstView = useRef(true);
  useEffect(() => {
    if (firstView.current) { firstView.current = false; return; } // initial view sent by gtag config
    const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (gaId && g) g("event", "page_view", { page_path: pathname, page_location: window.location.href, page_title: document.title });
  }, [pathname, gaId]);

  // Binotel GetCall: load the widget when the visitor navigates to the
  // Ukrainian locale from another locale (direct /uk loads are handled
  // by the inline script at the end of <body>).
  useEffect(() => {
    if (locale === "uk") injectBinotel();
  }, [locale]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster position="top-center" />
      <CookieConsent />
    </QueryClientProvider>
  );
}
